// SEO-driven landing pages for high-intent Google Analytics search queries.
// Each entry is a real page (not just a blog post) targeting one keyword cluster.
// Content is original and written specifically for the search intent — not
// AI-paraphrased fluff — because Google's Helpful Content Update penalises that.

export const SEO_LANDING_PAGES = {
  "google-analytics-for-beginners": {
    title: "Google Analytics for Beginners: The 2026 Plain-English Guide",
    h1: "Google Analytics for beginners — explained in plain English",
    description:
      "New to Google Analytics? This 5-minute beginner's guide explains the metrics that matter, the ones to ignore, and how to read your first GA4 report without the jargon.",
    keywords: [
      "google analytics for beginners",
      "google analytics tutorial",
      "ga4 for beginners",
      "how to use google analytics",
      "google analytics basics",
      "google analytics explained",
      "learn google analytics",
    ],
    cta: "Try Plainly free — turn GA4 into plain English",
    intro:
      "Google Analytics is one of the most powerful free tools on the internet — and one of the most confusing. If you've ever opened GA4 and immediately closed the tab because you couldn't figure out where to even look, you're not alone. This guide walks you through everything a small business owner actually needs to know, in plain English, without the dashboards-within-dashboards rabbit hole.",
    sections: [
      {
        heading: "What is Google Analytics, really?",
        body: [
          "Google Analytics is a free tool from Google that tells you who visits your website, what they do while they're there, and whether they take the actions you care about (buying something, signing up, requesting a quote). The current version is called GA4 — short for Google Analytics 4 — and it replaced the older Universal Analytics in July 2023.",
          "Think of it like a quiet assistant standing by the door of your shop, jotting down which doors people came through (Google search, Instagram, a link from another blog), what aisles they wandered into, and whether they bought anything. It doesn't capture names or personal details — just patterns.",
        ],
      },
      {
        heading: "The 5 metrics that actually matter",
        body: [
          "GA4 tracks hundreds of things. As a beginner, focus on these five and ignore the rest until you're comfortable:",
        ],
        bullets: [
          "**Users** — the count of distinct people who visited (Google estimates this from browsers and devices).",
          "**Sessions** — a single 'visit'. One user can have many sessions across multiple days.",
          "**Engaged sessions** — visits where someone stayed longer than 10 seconds, viewed multiple pages, or triggered a conversion. This is the closest thing to 'real interest'.",
          "**Conversions** — when someone does something you decided is valuable (a purchase, a form submit, a phone-number click).",
          "**Traffic acquisition** — where your visitors came from. Organic search? Direct? Social? Paid ads? This tells you where to spend your effort.",
        ],
      },
      {
        heading: "How to read your first GA4 report",
        body: [
          "Open analytics.google.com and click your property. The default 'Reports snapshot' is overwhelming, so skip it and go straight to **Reports → Acquisition → Traffic acquisition** in the left sidebar. This is the single most useful report in GA4.",
          "You'll see a table with channels (Organic Search, Direct, Referral, Social, Email, Paid Search) and a session count next to each. That's your first answer: where are your visitors actually coming from?",
          "Next, change the date range (top right) to 'Last 28 days' and compare it to the previous 28. If 'Organic Search' went up 20% week over week, your SEO is working. If 'Direct' is half your traffic, you have brand recognition. If 'Referral' from one specific site is huge, you've found an unlikely champion — go thank them.",
        ],
      },
      {
        heading: "Metrics you can safely ignore",
        body: [
          "Three GA4 metrics confuse beginners more than they help. You can come back to these later, but skip them on day one:",
        ],
        bullets: [
          "**Bounce rate** — GA4 calculates it differently than the old GA, and the number is rarely actionable on its own.",
          "**Average engagement time per session** — useful in aggregate, but easily skewed by a few outliers.",
          "**Event count** — GA4 logs everything as an 'event' (page views, scrolls, clicks). The raw number means nothing without context.",
        ],
      },
      {
        heading: "The fastest way to actually understand your data",
        body: [
          "Honestly, the hardest part of GA4 isn't reading the numbers — it's knowing which ones changed and why. That's exactly the problem Plainly was built to solve. You connect your Google Analytics (one click, read-only), pick a date range, and Plainly's AI hands you a 60-second plain-English readout: what went up, what went down, what likely caused it, and what to try next.",
          "It costs $18/month or $199/year for unlimited analyses — way cheaper than hiring a consultant, and faster than learning GA4 from scratch.",
        ],
      },
    ],
    faq: [
      {
        q: "Is Google Analytics still free in 2026?",
        a: "Yes — the standard version of GA4 is free for any website. Google also offers GA4 360, a paid enterprise tier with higher data limits and SLAs, but you almost certainly do not need it.",
      },
      {
        q: "Do I need to be technical to use GA4?",
        a: "No. Setting it up requires pasting a single tag into your site (most website builders like Shopify, Squarespace, and WordPress have a one-field box for it). Reading the reports doesn't require any technical knowledge — it just requires patience or a tool like Plainly that translates them for you.",
      },
      {
        q: "How long until GA4 starts showing data?",
        a: "Real-time data appears within minutes. Daily reports usually populate within 24–48 hours of installation.",
      },
    ],
  },

  "google-analytics-explained": {
    title: "Google Analytics Explained: A 2026 Owner's Guide to GA4",
    h1: "Google Analytics, explained for business owners",
    description:
      "Google Analytics explained in plain English. What every report means, which numbers matter for your business, and how to act on what you see — without the agency-speak.",
    keywords: [
      "google analytics explained",
      "what is google analytics",
      "ga4 explained",
      "google analytics simple explanation",
      "google analytics overview",
      "understanding google analytics",
    ],
    cta: "Try Plainly free — $18/mo for unlimited",
    intro:
      "Google Analytics is the largest free data tool on the internet, but most owners only ever skim the surface. This guide explains what each part of GA4 is actually for, written for the person running the business — not the agency you might one day hire.",
    sections: [
      {
        heading: "What Google Analytics tracks (and doesn't)",
        body: [
          "GA4 records anonymous patterns of behaviour on your site: page views, click paths, traffic sources, device types, rough location, and any 'events' you decide are valuable (purchases, sign-ups, downloads). It does NOT record names, email addresses, credit-card data, or any personally identifying information unless you specifically wire it up to send those — which is against Google's terms of service for the standard version.",
          "Translation: GA4 tells you 'a customer in Texas, on iPhone, found you via Google search and bought a $42 product.' It does not tell you that customer was Sarah Williams.",
        ],
      },
      {
        heading: "The four sections of GA4 you'll actually use",
        body: [
          "Ignore the rest until you're ready. These four cover 95% of small-business needs:",
        ],
        bullets: [
          "**Reports → Realtime** — who's on your site right now. Good for verifying your tracking works.",
          "**Reports → Acquisition → Traffic acquisition** — where your traffic comes from. The single most important screen in GA4.",
          "**Reports → Engagement → Pages and screens** — your best- and worst-performing pages.",
          "**Reports → Monetization → Ecommerce purchases** (if you run a store) — purchases, revenue, refunds.",
        ],
      },
      {
        heading: "Translating common GA4 phrases",
        bullets: [
          "**'Sessions'** = visits. One person visiting twice in a week = 2 sessions.",
          "**'Users'** = unique visitors. Same person visiting twice = 1 user.",
          "**'Engagement rate'** = the share of sessions that lasted >10 seconds OR had a conversion OR viewed 2+ pages. Higher is better.",
          "**'Conversion'** = an action you flagged as valuable. You define what counts.",
          "**'Channel'** = where the visit came from — Organic Search, Direct, Paid, Referral, Social, Email.",
          "**'Bounce rate'** = the inverse of engagement rate. In GA4 a 'bounce' = a session that didn't engage. Lower is better.",
        ],
      },
      {
        heading: "What changed when GA4 replaced Universal Analytics",
        body: [
          "The biggest change: GA4 is event-based, not session-based. In old Universal Analytics, everything was bucketed into sessions. In GA4, every interaction (a page view, a button click, a scroll) is its own event, and sessions are calculated from those events.",
          "Practically, this means your old GA numbers won't match your new GA4 numbers — and that's expected, not a bug. The new metric to anchor on is **engaged sessions**, which most closely matches what old GA called a 'session with engagement'.",
        ],
      },
      {
        heading: "Skip the dashboards — get a weekly summary",
        body: [
          "Most owners check GA4 once, get overwhelmed, and never go back. Plainly fixes that by emailing you a short, plain-English summary every Monday morning: 'Last week traffic was up 12% — most of it came from a viral LinkedIn post on Tuesday. Your /pricing page converted 3.4× better than your homepage.' That's it. No charts to decode.",
        ],
      },
    ],
    faq: [
      {
        q: "Is GA4 hard to set up?",
        a: "It takes about 5 minutes if your site is on Shopify, Squarespace, Wix, WordPress, or Webflow — those have built-in GA4 fields. Custom sites take a bit longer because you have to paste a tag manually.",
      },
      {
        q: "What's the difference between Google Analytics and Google Search Console?",
        a: "Analytics tracks what people do on your site. Search Console tracks how your site appears in Google search results (which queries you rank for, your click-through rate, indexing issues). Most businesses need both.",
      },
    ],
  },

  "how-to-understand-google-analytics": {
    title: "How to Understand Google Analytics (Without an Agency)",
    h1: "How to understand Google Analytics — without hiring an agency",
    description:
      "A practical 10-minute walkthrough for understanding Google Analytics on your own. Which screens to look at, which to ignore, and how to spot what changed week over week.",
    keywords: [
      "how to understand google analytics",
      "understanding google analytics",
      "how to read google analytics",
      "google analytics guide",
      "ga4 how to",
      "make sense of google analytics",
    ],
    cta: "Let Plainly read your GA for you — try free",
    intro:
      "Understanding Google Analytics is less about memorising what every button does and more about knowing where to look for the answer to the question you actually have. This guide is built around the three questions every business owner is really trying to answer when they open GA4.",
    sections: [
      {
        heading: "Question 1: Did more people visit this week than last week?",
        body: [
          "Go to **Reports → Acquisition → Traffic acquisition**. In the top right, click the date range and set it to 'Last 7 days'. Then click 'Compare' and pick 'Previous period'.",
          "You'll see two numbers side by side for each channel. The one you want is **Engaged sessions** — that's the closest GA4 metric to 'real visits'. If it's up versus last week, traffic is genuinely growing. If only 'Sessions' is up but 'Engaged sessions' is flat, you're probably getting more low-quality traffic (often bots or accidental clicks).",
        ],
      },
      {
        heading: "Question 2: Which page is doing the most work for me?",
        body: [
          "Go to **Reports → Engagement → Pages and screens**. The 'Views' column shows raw page views. The 'Engagement rate' column shows what share of visitors actually engaged with that page.",
          "Sort by Views first — that's your most-trafficked page. Then sort by Engagement rate — that's your most-loved page. Often they're not the same. The page with high traffic but low engagement is broken or off-message; the page with low traffic but high engagement is hiding from the world and you should drive more traffic to it.",
        ],
      },
      {
        heading: "Question 3: Why did the number change?",
        body: [
          "This is the hardest question GA4 answers, because the platform shows you what changed but rarely why. The investigation usually looks like:",
        ],
        bullets: [
          "Open **Acquisition → Traffic acquisition** and compare to previous period. Which channel moved the most?",
          "If it's Organic Search, cross-check Google Search Console to see which queries moved.",
          "If it's Referral, click 'Session source / medium' to find the specific referrer.",
          "If it's Direct, you probably launched something (email blast, podcast, press) — direct traffic catches everything GA can't categorise.",
        ],
      },
      {
        heading: "The 80/20 of GA4 for non-technical owners",
        body: [
          "If you only check three screens a month, make them: **Traffic acquisition** (where), **Pages and screens** (what), and **Conversions** (results). You can ignore explorations, audiences, attribution, BigQuery exports, and 90% of the rest until you have a specific question those tools answer.",
        ],
      },
      {
        heading: "When DIY stops being worth it",
        body: [
          "If you find yourself opening GA4 less than once a week, the problem isn't your discipline — it's the tool. Most owners only need the answer, not the dashboard. Plainly delivers that answer in a 60-second email every Monday so you stay informed without ever opening GA4 again.",
        ],
      },
    ],
    faq: [
      {
        q: "How often should I check Google Analytics?",
        a: "Weekly is plenty for most businesses. Daily checking causes overreaction to noise (single-day spikes rarely mean anything). Monthly is the minimum if you're running paid ads or actively doing SEO.",
      },
      {
        q: "Do I need to learn SQL or BigQuery for GA4?",
        a: "No, unless you're a 100-person enterprise running complex attribution. The standard GA4 reports cover everything a small business needs.",
      },
    ],
  },

  "ga4-for-small-business-owners": {
    title: "GA4 for Small Business Owners: The 2026 No-Jargon Guide",
    h1: "GA4 for small business owners",
    description:
      "GA4 explained for small business owners who run the marketing themselves. The metrics that matter for revenue, what to ignore, and how to spend 5 minutes a week, not 5 hours.",
    keywords: [
      "ga4 for small business",
      "ga4 for small business owners",
      "google analytics for small business",
      "google analytics small business",
      "small business analytics",
      "ga4 guide small business",
    ],
    cta: "Plainly is built for owners like you — start free",
    intro:
      "Small business owners don't need every GA4 metric — they need the ones that connect to revenue. This guide cuts the noise and shows you exactly which screens matter when you're running marketing yourself between actual customer work.",
    sections: [
      {
        heading: "Your 3-minute Monday morning check",
        body: [
          "Open GA4 on Monday, go to **Reports → Acquisition → Traffic acquisition**, set the date range to 'Last 7 days' compared to 'Previous period'. Look at three things:",
        ],
        bullets: [
          "Did **Engaged sessions** go up or down overall?",
          "Which **channel** moved the most? (Organic, Direct, Paid, Referral, Social, Email)",
          "Did **Conversions** keep pace? If sessions went up but conversions stayed flat, the new traffic was lower-intent.",
        ],
      },
      {
        heading: "Conversions you should set up this week",
        body: [
          "Most small businesses skip this and then wonder why GA4 feels useless. Spend 30 minutes setting up conversions for what actually matters:",
        ],
        bullets: [
          "**Contact form submitted** — if you're a service business, this is your #1 conversion.",
          "**Phone number clicked** — for any local business or service.",
          "**Email link clicked** — same.",
          "**'Buy' / 'Checkout' button clicked** — if you sell anything, even before tracking the full purchase.",
          "**Newsletter signup** — if you have a list.",
        ],
        bodyAfter: [
          "These are all set up under **Admin → Events** in GA4. Mark each one as a conversion using the toggle on the right.",
        ],
      },
      {
        heading: "The metric small businesses should care about most",
        body: [
          "Forget bounce rate. Forget impressions. The number that matters is your **conversion rate by channel** — what percentage of visitors from each traffic source actually converted.",
          "If Organic Search converts at 3% and Paid Search converts at 0.4%, you should be putting more money into SEO content and less into Google Ads. If Referral from one specific site is converting at 8%, you have a partnership opportunity sitting in plain sight.",
        ],
      },
      {
        heading: "What to ignore (for now)",
        body: [
          "Skip these until you have spare time — they confuse more than they help when you're starting out:",
        ],
        bullets: [
          "Attribution models (the defaults are fine for small businesses)",
          "Audiences (only useful if you're running Google Ads)",
          "Explorations (a power-user feature)",
          "Predictive metrics (require thousands of conversions to be reliable)",
          "BigQuery export (enterprise-grade overkill)",
        ],
      },
      {
        heading: "How Plainly saves small business owners 4+ hours a month",
        body: [
          "Plainly was built for the kind of owner who has 7 minutes between customer calls. You connect your Google Analytics once, set a default property, and every Monday morning you get a short email: 'This week your engaged sessions were up 14%. The lift came from a guest blog post that drove 220 sessions and 8 contact-form submissions. Your bounce rate is fine.' No dashboards. No jargon. Just the answer.",
        ],
      },
    ],
    faq: [
      {
        q: "I run a one-person business. Is GA4 worth the time?",
        a: "Yes — if you set up two or three conversions and check the data once a week. Without conversions, GA4 just tells you how many visitors came; with conversions, it tells you which ones turned into customers.",
      },
      {
        q: "Should I use Google Analytics or a paid alternative?",
        a: "Start with GA4 — it's free and handles 99% of small business needs. Paid tools like Fathom and Plausible are simpler to read but cost $14–29/month and lack conversion tracking depth. Plainly sits on top of GA4 and gives you the simplicity of a paid tool with the data depth of GA4.",
      },
    ],
  },

  "what-do-google-analytics-numbers-mean": {
    title: "What Do Google Analytics Numbers Actually Mean?",
    h1: "What do Google Analytics numbers actually mean?",
    description:
      "Decode every Google Analytics number in plain English. Users, sessions, engaged sessions, conversions, bounce rate — what they actually measure and what's a good or bad value.",
    keywords: [
      "what do google analytics numbers mean",
      "google analytics metrics explained",
      "google analytics meaning",
      "google analytics metrics definition",
      "google analytics terms",
      "ga4 metrics explained",
    ],
    cta: "Let Plainly translate the numbers for you",
    intro:
      "Google Analytics throws a dozen numbers at you on every screen. Some are critical, some are filler, and some are the same thing measured two different ways. This page is the cheat sheet — what each number means, and what 'good' looks like for a small business.",
    sections: [
      {
        heading: "Traffic metrics",
        bullets: [
          "**Users** — how many distinct people visited (Google estimates this from browser/device fingerprints). Good range: depends entirely on your industry. Track the trend, not the absolute number.",
          "**New users** — first-time visitors during the period. Good for measuring marketing reach.",
          "**Returning users** — visitors who'd been here before. High returning % = strong audience loyalty.",
          "**Sessions** — visits. One person = many sessions over time.",
          "**Engaged sessions** — visits that lasted >10s, had 2+ pages, or triggered a conversion. This is the most honest 'real visits' number GA4 has.",
        ],
      },
      {
        heading: "Engagement metrics",
        bullets: [
          "**Engagement rate** — the share of sessions that were 'engaged'. A healthy site is usually 50–70%. Below 40% is a problem; above 80% often means your tracking is configured to over-count.",
          "**Average engagement time** — average seconds spent per session. >1 minute is good for content sites, >30s is fine for landing pages.",
          "**Bounce rate** — the inverse of engagement rate. The number is rarely actionable on its own; look at engagement rate instead.",
          "**Views per session** — how many pages an average visitor sees. Higher = stickier site.",
        ],
      },
      {
        heading: "Acquisition metrics",
        bullets: [
          "**Channel** — the bucket your traffic came from. The standard ones: Organic Search, Direct, Paid Search, Organic Social, Paid Social, Email, Referral, Display.",
          "**Source** — the specific origin (e.g. 'google', 'instagram.com', 'twitter').",
          "**Medium** — how they arrived (e.g. 'organic', 'cpc', 'referral').",
          "**Landing page** — the first page they hit. The pages that show up here most often are your real homepage in Google's eyes.",
        ],
      },
      {
        heading: "Conversion metrics",
        bullets: [
          "**Conversions** — count of times someone did an action you flagged as valuable. You define what counts.",
          "**Conversion rate** — conversions ÷ sessions. 1–3% is normal for e-commerce; 5–10%+ is normal for high-intent forms (contact, demo, free trial).",
          "**Revenue** — total $ from purchases (if e-commerce tracking is wired up).",
          "**Average purchase revenue** — revenue ÷ purchasers. Track this over time, not absolutely.",
        ],
      },
      {
        heading: "Numbers you'll see but can mostly ignore",
        bullets: [
          "**Event count** — every interaction (page view, scroll, click) is an event. The raw total is meaningless out of context.",
          "**Total revenue** vs. **purchase revenue** — different aggregations of the same underlying number. Pick one and stick with it.",
          "**Item views** vs. **product views** — used in e-commerce reports; refer to the same thing.",
        ],
      },
    ],
    faq: [
      {
        q: "Why are my GA4 numbers different from my Shopify / payment processor numbers?",
        a: "Because GA4 relies on browser cookies and JavaScript firing correctly, while Shopify counts every completed checkout. Ad blockers, server-side checkouts, and slow connections all cause GA4 to undercount. Treat Shopify/Stripe/PayPal as the source of truth for revenue; treat GA4 as the source of truth for traffic patterns.",
      },
      {
        q: "What is a 'good' bounce rate?",
        a: "In GA4, a bounce rate under 50% is generally healthy, but it varies massively by page type. Blog posts often bounce at 70%+ and that's normal — the visitor came, read, left satisfied. Conversion pages should bounce much lower (under 40%).",
      },
    ],
  },

  "google-analytics-vs-ga4": {
    title: "GA4 vs Universal Analytics: What Changed and Why It Matters",
    h1: "GA4 vs Universal Analytics — what actually changed",
    description:
      "Universal Analytics shut down in July 2023 and GA4 replaced it. Here's exactly what changed, why your old numbers don't match, and what to do about it.",
    keywords: [
      "google analytics vs ga4",
      "ga4 vs universal analytics",
      "ga4 vs ua",
      "google analytics 4 vs universal analytics",
      "ga4 differences",
      "universal analytics to ga4",
    ],
    cta: "Make sense of your new GA4 data — try Plainly",
    intro:
      "Google sunset Universal Analytics on July 1, 2023, and replaced it with GA4 — a completely different model. If your reports look unfamiliar, the metrics don't match what they used to, or you can't find features that were obvious before, you're not crazy. Here's the practical guide to what changed and what to do.",
    sections: [
      {
        heading: "The fundamental change: events, not sessions",
        body: [
          "Universal Analytics organised everything into sessions. A session was 30 minutes of activity; every page view, click, and event got bucketed under a session ID.",
          "GA4 organised everything into events. Every page view is an event. Every scroll is an event. Every button click is an event. Sessions are calculated from those events, not the other way around.",
          "Practically: your old GA 'sessions' count and your new GA4 'sessions' count won't match. They're measuring slightly different things. Don't try to reconcile them — anchor on GA4 going forward.",
        ],
      },
      {
        heading: "Metrics that were renamed or removed",
        bullets: [
          "**Pageviews → Views** (still tracked, just renamed)",
          "**Bounce rate** — calculated differently in GA4. UA bounce = single-page session. GA4 bounce = session that didn't 'engage' (10s, multi-page, or conversion). Numbers will be very different.",
          "**Goals → Conversions** (renamed; same idea)",
          "**Behaviour Flow** — removed entirely. The closest replacement is 'Path exploration' under Explorations.",
          "**Site Search reports** — gone from the standard reports; must be rebuilt manually in Explorations.",
        ],
      },
      {
        heading: "New things GA4 does that UA didn't",
        bullets: [
          "**Cross-device tracking by default** — UA needed paid Google Signals; GA4 includes it free.",
          "**Built-in event auto-tracking** — scrolls, outbound clicks, file downloads, video views all tracked automatically.",
          "**Predictive metrics** — purchase probability, churn probability (requires lots of conversions).",
          "**Free BigQuery export** — UA reserved this for the $150k/yr enterprise tier.",
          "**No data sampling on most reports** — UA sampled aggressively above 500k sessions.",
        ],
      },
      {
        heading: "What to do if you still have old UA data you want to keep",
        body: [
          "Google deleted the UA UI in July 2024. If you didn't export your historical UA data, it's gone. If you did export to BigQuery or downloaded CSVs, keep them as a reference but don't try to merge them into GA4 — the data models are too different.",
        ],
      },
      {
        heading: "Best practice: pretend UA never existed",
        body: [
          "The single fastest way to make peace with GA4 is to treat it as a fresh start. Don't compare GA4 metrics to your old UA benchmarks. Build new benchmarks from your current GA4 numbers — what counts as a 'good' engagement rate or conversion rate for your specific business — and track changes from there.",
        ],
      },
    ],
    faq: [
      {
        q: "Can I still access my old Universal Analytics data?",
        a: "No — Google deleted the UA interface and APIs on July 1, 2024. The only remaining UA data is whatever you exported to BigQuery or CSV before that date.",
      },
      {
        q: "Why are my GA4 conversion numbers lower than my old UA goal numbers?",
        a: "Several reasons: GA4 attributes conversions differently (event-based vs. session-based), GA4's default consent settings are stricter (so visitors who reject cookies don't get tracked), and GA4 doesn't double-count the way UA goals sometimes did.",
      },
    ],
  },

  // -------------------------------------------------------------------------
  // Iter 13 — two new persona-targeted SEO landing pages. These complement
  // the existing 6 by targeting (a) the non-marketer persona and (b) the
  // Shopify-owner vertical, both of which represent large search-volume
  // clusters with weaker existing content on the SERP.
  // -------------------------------------------------------------------------
  "how-to-read-google-analytics-without-being-a-marketer": {
    title: "How to Read Google Analytics Without Being a Marketer (2026 Guide)",
    h1: "How to read Google Analytics without being a marketer",
    description:
      "You run the business, not the marketing department. This plain-English guide shows you how to read Google Analytics (GA4) in 10 minutes a week, without learning a single marketing acronym — the 4 numbers that actually matter and what each one tells you.",
    keywords: [
      "how to read google analytics",
      "google analytics for non marketers",
      "google analytics for beginners",
      "understand google analytics without marketing",
      "ga4 for business owners",
      "google analytics simple",
      "read ga4 reports",
      "google analytics no jargon",
    ],
    cta: "Try Plainly free — turn GA4 into plain English",
    intro:
      "If you're a founder, owner, freelancer, or operator — anyone whose job is the business, not the marketing — Google Analytics was not designed for you. Every report assumes you've memorised a glossary that takes most marketers two years on the job to absorb. This guide skips the glossary. Instead, we'll walk through the only four numbers that matter for someone who just wants to know if their website is doing its job, what each number actually tells you, and how to spot trouble in under 10 minutes a week.",
    sections: [
      {
        heading: "First, the truth: 95% of GA4 is built for someone else",
        body: [
          "GA4 was designed for marketing teams running paid ad campaigns at scale. Most of its dashboards, segments, audiences, attribution models, and event configurations exist to serve that audience. If you're not in that audience, you can safely ignore 95% of the interface. The trick is knowing which 5% to actually look at.",
          "This isn't a knock on GA4 — it's a free product built by Google for advertisers. Expecting it to be intuitive for non-advertisers is like expecting Final Cut Pro to be intuitive for someone who just wants to trim a phone video. The tool is fine. It's just not built for you.",
        ],
      },
      {
        heading: "The 4 numbers that actually matter (for business owners)",
        body: [
          "Forget every other number for the first six months. Watch these four, weekly, and you'll know more about your website than 80% of small-business owners:",
        ],
        bullets: [
          "**Sessions** — how many visits you got. The simplest top-line health number. Up is usually good.",
          "**Users** — how many distinct people. Sessions ÷ users tells you how often each person comes back. Higher = more loyalty.",
          "**Conversions** — how many people did the thing you wanted (bought, signed up, called). If you only watch one number, watch this one.",
          "**Conversion rate** — conversions ÷ sessions × 100. The single best measure of whether your website actually converts visitors into customers.",
        ],
      },
      {
        heading: "Where to find them (one click each)",
        body: [
          "Open analytics.google.com and click your property. From the home screen:",
        ],
        bullets: [
          "**Sessions and users**: Reports → Reports snapshot. Top-left tiles. Done.",
          "**Conversions**: Reports → Engagement → Key events. Pick your conversion event from the table.",
          "**Conversion rate**: Reports → Acquisition → Traffic acquisition. Add the 'Session conversion rate' column (✏️ pencil icon → Metrics → +Add)",
        ],
        bodyAfter: [
          "That's it. Three clicks, four numbers, no marketing degree required.",
        ],
      },
      {
        heading: "The weekly 10-minute routine",
        body: [
          "Once a week — Monday morning is best, before email — open GA4 and do exactly this:",
        ],
        bullets: [
          "**Minute 1-2**: Look at sessions for the last 7 days vs. previous 7 days. Up or down? By how much?",
          "**Minute 3-4**: Look at conversions same way. Up or down? By how much?",
          "**Minute 5-6**: If conversions changed, open Reports → Acquisition → Traffic acquisition. Look at which channel (Organic Search, Direct, Social, Referral) changed the most. That's almost always your answer.",
          "**Minute 7-10**: Write yourself two sentences in a notes file. Example: 'Conversions up 12% this week, driven mostly by Organic Search +24% — likely the blog post we shipped Tuesday. Stay the course.' Done.",
        ],
        bodyAfter: [
          "You don't need to understand engagement rate, bounce rate, attribution models, segments, audiences, custom channel groups, or any of the other 200 things GA4 offers. You need to know whether visitors are coming, where from, and whether they're converting. Those four numbers cover it.",
        ],
      },
      {
        heading: "What to do when something looks wrong",
        body: [
          "If a number drops 20% or more in a single week, don't panic. Three checks, in order:",
        ],
        bullets: [
          "**Did anything change on the site?** Recent design tweaks, plugin updates, theme changes — all common causes of metric drops.",
          "**Did one channel collapse?** Look at the traffic-acquisition breakdown. If Organic Search alone dropped, you might have a Google indexing issue. If Social dropped, an ad campaign ended.",
          "**Is it Easter, Christmas, or a Tuesday?** Seasonal swings are normal. Compare to the same week last year before assuming there's a problem.",
        ],
      },
      {
        heading: "The shortcut: outsource the reading entirely",
        body: [
          "If even 10 minutes a week feels like too much, that's exactly the gap Plainly was built to close. You connect your Google Analytics once (read-only, one-click consent), and every Monday morning we email you a one-paragraph plain-English summary of exactly what changed week-over-week, what likely caused it, and what to do about it. No dashboards. No jargon. No login required to read the email.",
          "It's $18/month or $199/year for unlimited analyses. About the same as a single hour with a consultant, with the difference that Plainly does it for you every week, forever.",
        ],
      },
    ],
    faq: [
      {
        q: "I'm not technical. Can I really use Google Analytics?",
        a: "Yes. You don't need to install anything, write any code, or understand any acronyms — just sign in, connect your site (most platforms like Shopify, Squarespace, and WordPress have a one-field setting for this), and look at the four numbers above weekly.",
      },
      {
        q: "Do I need a marketer or agency to interpret the numbers for me?",
        a: "No. The four numbers in this guide are self-interpreting. If sessions go down, fewer people visited. If conversions go down, fewer people did the thing. You don't need a marketing degree to act on that — you need to know which lever to pull (usually: traffic source, page experience, or offer), which is the part the agency adds. For 95% of small businesses, knowing the numbers is enough.",
      },
      {
        q: "Why does Google Analytics feel so overwhelming?",
        a: "Because it was built for ad agencies tracking 50 campaigns across 200 audiences with multi-touch attribution. You're trying to answer a much simpler question ('is my website working?'), and the tool's defaults are tuned for a much more complex one.",
      },
      {
        q: "Is there a simpler alternative?",
        a: "Plausible, Fathom, and Simple Analytics are popular privacy-friendly alternatives with much cleaner interfaces. They cost $9–$30/month. The downside is they don't have the same conversion-tracking depth as GA4. If you don't care about per-channel attribution, they're great. If you do, GA4 (with a translator like Plainly) is still the best free option.",
      },
    ],
  },

  "google-analytics-for-shopify-owners": {
    title: "Google Analytics for Shopify Owners — The 5 GA4 Reports That Actually Matter",
    h1: "Google Analytics for Shopify owners — explained",
    description:
      "Running a Shopify store? GA4 has hundreds of reports — only 5 matter for ecommerce. Here's exactly which reports to bookmark, what each tells you about your store, and how to spot revenue problems before they hit your bank account.",
    keywords: [
      "google analytics for shopify",
      "ga4 shopify",
      "shopify google analytics 4",
      "shopify ga4 reports",
      "ecommerce google analytics",
      "shopify conversion rate ga4",
      "ga4 ecommerce tracking",
      "shopify analytics guide",
    ],
    cta: "Try Plainly free — Monday emails for your Shopify store",
    intro:
      "Shopify gives you its own analytics dashboard, but if you're trying to grow past $50K/month you'll quickly outgrow it. Google Analytics 4, properly set up alongside Shopify, gives you the channel-level attribution and conversion-rate detail that Shopify Analytics simply doesn't. The catch: GA4 ships with 200+ reports, and 195 of them are useless for an ecommerce store. This guide covers the 5 that actually matter — bookmarked once and checked weekly, they'll tell you everything you need to know to grow.",
    sections: [
      {
        heading: "First: set up GA4 properly on Shopify (5 minutes)",
        body: [
          "Shopify shipped a native GA4 integration in 2023, so installation is now a one-field setting — no Google Tag Manager required for most stores. To set it up:",
        ],
        bullets: [
          "Create a GA4 property at analytics.google.com if you don't have one. Copy the measurement ID (starts with G-).",
          "In your Shopify admin, go to Online Store → Preferences → Google Analytics → Set up Google. Paste the G- measurement ID.",
          "Enable 'Use enhanced ecommerce' — this is the box that makes purchase tracking work automatically.",
          "Wait 24-48 hours, then place a test order to verify conversions appear in GA4 → Reports → Realtime.",
        ],
        bodyAfter: [
          "If you're using a third-party Shopify theme, the tracking should work without changes — Shopify's enhanced ecommerce integration fires the standard GA4 ecommerce events (`view_item`, `add_to_cart`, `begin_checkout`, `purchase`) regardless of theme.",
        ],
      },
      {
        heading: "Report #1: Reports → Acquisition → Traffic acquisition",
        body: [
          "The single most important report for any Shopify store. Shows you where your buyers came from — Organic Search, Direct, Social, Referral, Email, Paid Search — and how much revenue each channel drove.",
          "What to look for: which channel is driving the most revenue, and which channel has the best **session conversion rate** (revenue per session, or % of sessions that became purchases). A channel sending 5,000 sessions/month at a 0.5% conversion rate is doing less for you than a channel sending 1,000 sessions at 4% — even though the first one looks bigger by traffic.",
        ],
      },
      {
        heading: "Report #2: Reports → Engagement → Pages and screens",
        body: [
          "Your product pages, collection pages, and landing pages, ranked by traffic and engagement. This is where you find the page-level winners and losers.",
          "What to look for: a product page with high traffic but low add-to-cart rate is a copy or photography problem. A product page with high add-to-cart but low purchase rate is a checkout, shipping, or trust problem. Sort by sessions to find your high-traffic pages first, then drill into each.",
        ],
      },
      {
        heading: "Report #3: Reports → Monetization → Ecommerce purchases",
        body: [
          "The product-level revenue report — every SKU sold, in order of revenue. Vital for inventory and marketing decisions.",
          "What to look for: which products are pulling the most revenue this month vs last month? Which products got a big spike from a single campaign and would benefit from a follow-up promotion? Which products with high page views have low purchase counts (worth a copy or pricing review)?",
        ],
      },
      {
        heading: "Report #4: Reports → Monetization → Purchase journey",
        body: [
          "The funnel report — what % of visitors view a product, add to cart, begin checkout, and complete a purchase. This is the most actionable single report in GA4 for ecommerce.",
          "What to look for: the biggest drop-off step. If 100% view a product, 35% add to cart, 18% begin checkout, but only 6% complete purchase — your checkout itself is leaking money. If 100% view, 12% add to cart, 11% begin checkout, 10% complete — your product pages are the bottleneck. Each store has its own pattern; the diagnosis is wherever the biggest single-step drop happens.",
        ],
      },
      {
        heading: "Report #5: Reports → Engagement → Landing page",
        body: [
          "Lists every URL someone first landed on, ranked by sessions. Critical for SEO and paid-ads decisions.",
          "What to look for: landing pages with high sessions and low conversion rate are SEO winners (you're getting traffic) with conversion-rate problems (you're not converting it). The fix is usually one of: better headline, clearer hero image, lower-friction CTA, or trust signals (reviews, guarantees). For paid landing pages, the same logic applies — high sessions but low conversion rate means your traffic is fine but your offer/page needs work.",
        ],
      },
      {
        heading: "The Shopify-specific gotchas",
        body: [
          "Three things commonly trip up Shopify owners reading GA4 for the first time:",
        ],
        bullets: [
          "**Revenue in GA4 won't exactly match Shopify Admin.** GA4 only counts orders from sessions where its tracking fired. Adblockers, slow-loading pages, and consent rejections cause some orders to go untracked. A 5-15% gap between GA4 revenue and Shopify revenue is normal — anything bigger means a tracking issue worth investigating.",
          "**Shopify checkout used to live on a different domain.** Pre-2023 Shopify stores had their checkout on `checkout.shopify.com`, which broke session continuity. Shopify migrated to single-domain checkout in late 2023 — if you're still on the old setup, conversions in GA4 will look dramatically lower than reality.",
          "**Refunds and cancellations don't subtract automatically.** GA4 records the purchase event when the order is placed. If you refund the order, GA4 doesn't deduct that revenue unless you explicitly fire a `refund` event. For most small stores, the simpler fix is to compare GA4 revenue to net Shopify revenue (after refunds) once a month rather than expecting exact matches.",
        ],
      },
      {
        heading: "The 5-minute Shopify weekly check",
        body: [
          "Once a week, in this order, check exactly these:",
        ],
        bullets: [
          "Sessions and total revenue (Reports → Reports snapshot)",
          "Conversion rate by channel (Traffic acquisition → Session conversion rate column)",
          "Top 5 selling products this week (Ecommerce purchases, sort by revenue)",
          "Biggest drop-off step in the funnel (Purchase journey)",
          "Anything unusual? (e.g., one landing page suddenly carrying twice its normal traffic, or one channel dropping by 50%)",
        ],
        bodyAfter: [
          "Five reports, five minutes, every Monday. That's the entire weekly analytics routine for a Shopify owner doing $10K–$500K/month in revenue.",
        ],
      },
      {
        heading: "Or — let Plainly do it for you",
        body: [
          "Plainly was built to replace exactly this weekly routine. Connect your GA4 once (one-click, read-only). Every Monday morning, you get an email with a plain-English summary of what changed week-over-week, which channel and which products drove the change, and what to do about it. Built specifically for non-marketers running their own businesses — no jargon, no dashboards, no acronyms.",
          "Try the free demo from the homepage and see the kind of weekly summary you'd get for your store.",
        ],
      },
    ],
    faq: [
      {
        q: "Do I need both Shopify Analytics and Google Analytics?",
        a: "Yes, for most stores. Shopify Analytics is great for order-level data (which Shopify natively tracks better than anyone). GA4 is great for traffic-source attribution, landing-page performance, and funnel analysis. They complement each other — neither one replaces the other.",
      },
      {
        q: "Why is my Shopify revenue higher than my GA4 revenue?",
        a: "GA4 only records purchases where its tracking fired successfully. Ad blockers, slow page loads on the order-confirmation page, consent rejections, and stripped-out tracking scripts all cause a gap. A 5-15% gap is normal. Anything bigger usually indicates a tracking problem worth investigating with the Google Tag Assistant Chrome extension.",
      },
      {
        q: "Can GA4 replace Shopify's marketing reports?",
        a: "Partially. GA4's attribution is more sophisticated than Shopify's, especially for multi-channel paths. But Shopify's customer-level reports (lifetime value, repeat-purchase rate) are still better than GA4's for ecommerce. Use the right tool for the right question.",
      },
      {
        q: "Do I need GA4 if I'm running Shopify ads through Shopify's own Audiences product?",
        a: "Strongly recommended. Shopify Audiences gives you Meta and Google ad audiences but doesn't give you the channel-comparison view (paid social vs. organic search vs. email). GA4 is where you see how the channels stack up against each other.",
      },
    ],
  },
};

// Helper: get all slugs (used for routing + sitemap).
export const SEO_LANDING_SLUGS = Object.keys(SEO_LANDING_PAGES);
